import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import SchemaEditorHeader from './shared/SchemaEditorHeader'
import {
  ReactFlow,
  Background,
  ConnectionMode,
  Panel,
  reconnectEdge,
  NodeResizer,
  useEdgesState,
  useNodesState,
  type Node,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
  type Viewport,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import {
  addFloorPlanItem,
  addFloorPlanShape,
  createFloorPlanLayer,
  deleteFloorPlanLayer,
  getFloorPlan,
  removeFloorPlanItem,
  updateFloorPlanItem,
  updateFloorPlanLayer,
  type FloorPlanItem,
  type FloorPlanLayer,
  type FloorPlanPayload,
  type FloorPlanShape,
  type FloorPlanNodeProperties,
  type FloorPlanShapeStyle,
  type FloorPlanShapeType,
} from '../api/floorPlan'
import {
  ACTIVE_LAYER_STORAGE_KEY,
  algorithmFromLayerString,
  isLayerLocked,
  isLayerVisible,
  layerById,
  resolveItemLayerId,
} from './floorPlanLayerHelpers'
import { getAvailableDevicesForSchemaType } from '../api/deviceInstances'
import { EditableEdgeComponent, EditableEdge, type EdgeLabelData } from '../edges/EditableEdge'
import { ConnectionLine } from '../edges/ConnectionLine'
import { Algorithm, COLORS, DEFAULT_ALGORITHM } from '../edges/EditableEdge/constants'
import { ColorPickerField } from './shared/ColorPickerField'
import SchemaElementInfoAside from './shared/SchemaElementInfoAside'
import { FreehandOverlay, FreehandSvg, normalizeFreehandStyle, type FloorPlanFreehandStyle } from './floorPlanFreehand'
import { useAuth } from '../AuthContext'
import { createSchemaManager } from './shared/SchemaManager'
import { apiClient } from '../api/client'
import { type PermissionRow } from '../api/admin'
import { getSpecificationInterfaces, type SpecificationInterface } from '../api/specificationInterfaces'
import { getConnections, createConnection, deleteConnection, updateConnection } from '../api/connections'

// Removed uploadErrorMessage for network schema

type SelectedDevice = FloorPlanItem | null
const DEVICE_DRAG_TYPE = 'text/plain'
const FLOOR_PLAN_EDGE_SAVE_DELAY_MS = 500
const DEFAULT_EDGE_STROKE_WIDTH = 2

type LineStylePreset = 'solid' | 'dashed' | 'dotted' | 'dashdot' | 'longdash' | 'custom'

type ShapePreset = {
  type: FloorPlanShapeType
  label: string
  fill: string
  stroke: string
  width: number
  height: number
}

const SHAPE_PRESETS: ShapePreset[] = [
  { type: 'circle', label: 'Круг', fill: '#f8fafc', stroke: '#64748b', width: 88, height: 88 },
  { type: 'round-rectangle', label: 'Скруглённый блок', fill: '#f8fafc', stroke: '#64748b', width: 120, height: 80 },
  { type: 'square', label: 'Квадрат', fill: '#f8fafc', stroke: '#64748b', width: 88, height: 88 },
  { type: 'hexagon', label: 'Шестиугольник', fill: '#f8fafc', stroke: '#64748b', width: 112, height: 80 },
  { type: 'diamond', label: 'Ромб', fill: '#f8fafc', stroke: '#64748b', width: 88, height: 88 },
  { type: 'document', label: 'Документ', fill: '#f8fafc', stroke: '#64748b', width: 110, height: 84 },
  { type: 'cylinder', label: 'Цилиндр', fill: '#f8fafc', stroke: '#64748b', width: 108, height: 84 },
  { type: 'triangle', label: 'Треугольник', fill: '#f8fafc', stroke: '#64748b', width: 96, height: 92 },
  { type: 'parallelogram', label: 'Параллелограмм', fill: '#f8fafc', stroke: '#64748b', width: 112, height: 80 },
  { type: 'plus', label: 'Плюс', fill: '#f8fafc', stroke: '#64748b', width: 92, height: 92 },
]

function createShapeStyle(preset: ShapePreset): FloorPlanShapeStyle {
  return {
    kind: 'shape',
    shapeType: preset.type,
    label: preset.label,
    width: preset.width,
    height: preset.height,
    fill: preset.fill,
    stroke: preset.stroke,
    strokeWidth: 2,
  }
}

function normalizeShapeType(type: unknown): ShapePreset {
  if (type === 'ellipse') return SHAPE_PRESETS[0]
  if (type === 'rectangle') return SHAPE_PRESETS[2]
  return SHAPE_PRESETS.find((item) => item.type === type) ?? SHAPE_PRESETS[1]
}

function normalizeShapeStyle(raw: unknown): FloorPlanShapeStyle {
  const value = raw && typeof raw === 'object' ? raw as Partial<FloorPlanShapeStyle> : {}
  const preset = normalizeShapeType(value.shapeType)
  return {
    ...createShapeStyle(preset),
    ...value,
    kind: 'shape',
    shapeType: preset.type,
    label: typeof value.label === 'string' ? value.label : preset.label,
    width: Math.max(24, Number(value.width) || preset.width),
    height: Math.max(24, Number(value.height) || preset.height),
    strokeWidth: Math.max(1, Number(value.strokeWidth) || 2),
  }
}

function isFreehandStyle(raw: unknown): raw is FloorPlanFreehandStyle {
  return Boolean(raw && typeof raw === 'object' && (raw as { kind?: unknown }).kind === 'freehand')
}

function normalizeFloorPlanProperties(raw: unknown): FloorPlanNodeProperties {
  return isFreehandStyle(raw) ? normalizeFreehandStyle(raw) : normalizeShapeStyle(raw)
}

const clampEdgeLabelPosition = (value: number): number => Math.min(0.95, Math.max(0.05, value))

function createEdgeLabel(text: string, index: number): EdgeLabelData {
  return {
    id: `label-${Date.now()}-${Math.round(Math.random() * 100000)}`,
    text,
    position: clampEdgeLabelPosition(0.5 + ((index % 5) - 2) * 0.12),
  }
}

const LINE_TYPE_LABELS: Record<Algorithm, string> = {
  [Algorithm.Linear]: 'Прямая',
  [Algorithm.Step]: 'Г-образная / ступенчатая',
  [Algorithm.CatmullRom]: 'Кривая Catmull-Rom',
  [Algorithm.BezierCatmullRom]: 'Кривая Безье',
}

const LINE_STYLE_OPTIONS: { value: LineStylePreset; label: string; dasharray?: string }[] = [
  { value: 'solid', label: 'Сплошная' },
  { value: 'dashed', label: 'Пунктир', dasharray: '8 4' },
  { value: 'dotted', label: 'Точки', dasharray: '2 4' },
  { value: 'dashdot', label: 'Штрих-пунктир', dasharray: '10 4 2 4' },
  { value: 'longdash', label: 'Длинный пунктир', dasharray: '16 6' },
  { value: 'custom', label: 'Свой пунктир' },
]

function getLineStylePreset(strokeDasharray: unknown): LineStylePreset {
  if (typeof strokeDasharray !== 'string' || strokeDasharray.trim() === '') return 'solid'
  const normalized = strokeDasharray.trim().replace(/\s+/g, ' ')
  const matched = LINE_STYLE_OPTIONS.find((option) => option.dasharray === normalized)
  return matched?.value ?? 'custom'
}

function getDashNumbers(strokeDasharray: unknown): { dash: number; gap: number } {
  if (typeof strokeDasharray !== 'string') return { dash: 8, gap: 4 }
  const [dash, gap] = strokeDasharray.split(/\s+/).map((value) => Number(value))
  return {
    dash: Number.isFinite(dash) && dash > 0 ? dash : 8,
    gap: Number.isFinite(gap) && gap > 0 ? gap : 4,
  }
}



// Removed ImageNode for network schema

function DeviceNode({ data }: any) {
  const {
    device_instance_id,
    specification_id,
    position_label,
    spec_name,
    description,
    manufacturer,
    model,
    is_reserve = false,
    isActive,
    networkType
  } = data

  const [interfaces, setInterfaces] = useState<SpecificationInterface[]>([])

  useEffect(() => {
    if (specification_id) {
      const load = async () => {
        try {
          const res = await getSpecificationInterfaces(specification_id)
          setInterfaces(res || [])
        } catch (e) {
          console.error('Error fetching interfaces for spec', specification_id, e)
        }
      }
      load()
    }
  }, [specification_id])

  // Filter interfaces by current network type (if networkType is set)
  const filteredInterfaces = useMemo(() => {
    if (!networkType) return interfaces
    return interfaces.filter((iface) => iface.interface_type_code === networkType)
  }, [interfaces, networkType])

  // Color schemes based on reserve status
  const deviceColors = {
    border: is_reserve ? 'border-gray-600' : 'border-blue-600',
    bg: is_reserve ? 'bg-gray-50' : 'bg-blue-50',
    bgSecondary: is_reserve ? 'bg-gray-100' : 'bg-blue-100',
    borderSecondary: is_reserve ? 'border-gray-200' : 'border-blue-200',
    text: is_reserve ? 'text-gray-700' : 'text-blue-700',
    textPrimary: is_reserve ? 'text-gray-800' : 'text-blue-800',
    textSecondary: is_reserve ? 'text-gray-600' : 'text-blue-600',
    handleBg: is_reserve ? 'bg-gray-100' : 'bg-blue-100',
    handleBorder: is_reserve ? 'border-gray-500' : 'border-blue-500'
  }

  return (
    <div
      className={`bg-white border-2 ${deviceColors.border} ${isActive ? 'ring-2 ring-blue-300' : ''} rounded shadow-lg select-none flex`}
      style={{ minWidth: '120px', width: 'max-content', minHeight: '85px' }}
    >
      <div className="flex-1 flex flex-col justify-between">
        {/* TOP contacts with handles */}
        {filteredInterfaces.length > 0 && (
          <div className="flex flex-row flex-nowrap justify-between items-center px-2 py-1 gap-x-4 border-b border-gray-200 bg-gray-50">
            {filteredInterfaces.map((iface) => (
              <div key={iface.id} className="flex flex-col items-center relative whitespace-nowrap min-w-[32px] px-1">
                <div className={`w-3 h-3 rounded-full border-2 ${deviceColors.handleBorder} ${deviceColors.handleBg} relative`}>
                  <Handle
                    type="target"
                    position={Position.Top}
                    id={`${iface.id}-target`}
                    className="schema-target-handle !w-px !h-px !bg-white !border-0"
                    style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                  />
                  <Handle
                    type="source"
                    position={Position.Top}
                    id={`${iface.id}-source`}
                    className="schema-source-handle !w-px !h-px !bg-white !border-0"
                    style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                  />
                </div>
                <div className="text-[8px] font-medium text-gray-700 mt-0.5 whitespace-nowrap" style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>
                  {iface.chassis_label || iface.os_name || `${iface.id}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Name and specification */}
        <div className={`px-2 py-1 text-center border-b ${deviceColors.borderSecondary}`}>
          <div className={`font-bold ${deviceColors.textPrimary}`} style={{ fontSize: '11px', wordBreak: 'break-all' }}>
            {position_label || `ID ${device_instance_id}`}
          </div>
          <div className={`font-bold ${deviceColors.text}`} style={{ fontSize: '10px', wordBreak: 'break-all', marginTop: '2px' }}>
            {spec_name}
          </div>
        </div>

        {/* Manufacturer and Model */}
        {(manufacturer || model) && (
          <div className={`px-2 py-1 text-center border-b ${deviceColors.borderSecondary} bg-blue-50`}>
            <div className={`font-bold ${deviceColors.text}`} style={{ fontSize: '10px', wordBreak: 'break-all' }}>
              {manufacturer && <span>{manufacturer}</span>}
              {manufacturer && model && <span> | </span>}
              {model && <span>{model}</span>}
            </div>
          </div>
        )}

        {/* Description */}
        <div className="px-1 py-1 text-center border-b border-gray-200 flex-1 flex items-center justify-center" style={{ minHeight: '35px' }}>
          <div className={`font-medium ${deviceColors.textSecondary}`} style={{ fontSize: '10px' }}>
            {description && description.trim() ? description : ''}
          </div>
        </div>

        {/* IP and MAC placeholder */}
        <div className="px-1 py-0.5 text-center border-t border-gray-200 bg-blue-50">
          <div className="font-medium text-blue-600" style={{ fontSize: '10px' }}>—</div>
          <div className="text-gray-500" style={{ fontSize: '10px' }}>—</div>
        </div>
      </div>
    </div>
  )
}

function CableNode({ data }: any) {
  const markerSize = data.markerSize || 10
  const scale = typeof data.layerStyle?.markerScale === 'number' ? data.layerStyle.markerScale : Number(data.layerStyle?.markerScale) || 1
  const width = Math.max(markerSize * 8, 80) * scale
  const height = Math.max(markerSize * 3, 30) * scale
  const svgMarkup = data.layerStyle?.svgMarkup as string | undefined
  const svgColor = (data.layerStyle?.svgColor as string) || '#16a34a'
  const labelFs = typeof data.layerStyle?.labelFontSize === 'number' ? data.layerStyle.labelFontSize : Number(data.layerStyle?.labelFontSize) || 11
  const labelBg = (data.layerStyle?.labelBgColor as string) || 'rgba(255,255,255,0.9)'
  const maskStyle = svgMarkup
    ? {
        backgroundColor: svgColor,
        WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(svgMarkup)}")` as const,
        maskImage: `url("data:image/svg+xml,${encodeURIComponent(svgMarkup)}")` as const,
        WebkitMaskSize: 'contain' as const,
        maskSize: 'contain' as const,
        WebkitMaskRepeat: 'no-repeat' as const,
        maskRepeat: 'no-repeat' as const,
        WebkitMaskPosition: 'center' as const,
        maskPosition: 'center' as const,
      }
    : undefined
  return (
    <div style={{ position: 'relative', width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Handle type="target" position={Position.Left} style={{ opacity: 0, width: 16, height: 16, left: -8, top: '50%', transform: 'translateY(-50%)' }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0, width: 16, height: 16, right: -8, left: 'auto', top: '50%', transform: 'translateY(-50%)' }} />
      {svgMarkup && maskStyle ? (
        <div className="w-full h-full rounded-md border border-black/20 shadow-sm flex items-center justify-center" style={maskStyle}>
          <span className="sr-only">{data.label}</span>
        </div>
      ) : (
        <div
          className={`w-full h-full rounded-md border-2 flex items-center justify-center shadow-sm ${data.isActive ? 'bg-red-600 border-red-800 ring-2 ring-red-300 text-white' : 'bg-green-600 border-green-800 text-white'}`}
          style={{ fontSize: `${labelFs}px`, fontWeight: 'bold', padding: '0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {data.label}
        </div>
      )}
      {svgMarkup && (
        <span
          className="absolute left-1/2 -bottom-5 -translate-x-1/2 whitespace-nowrap font-semibold text-black px-1 rounded pointer-events-none text-[10px]"
          style={{ fontSize: Math.max(9, labelFs - 1), backgroundColor: labelBg }}
        >
          {data.label}
        </span>
      )}
    </div>
  )
}

function ShapeSvg({ properties, className }: { properties: FloorPlanShapeStyle; className?: string }) {
  const width = properties.width
  const height = properties.height
  const strokeInset = Math.max(1, properties.strokeWidth / 2)
  const commonShapeProps = {
    fill: properties.fill,
    stroke: properties.stroke,
    strokeWidth: properties.strokeWidth,
    vectorEffect: 'non-scaling-stroke' as const,
  }

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className={className ?? 'block drop-shadow-sm'} preserveAspectRatio="none">
      {properties.shapeType === 'circle' ? (
        <ellipse cx={width / 2} cy={height / 2} rx={Math.max(1, (width - properties.strokeWidth) / 2)} ry={Math.max(1, (height - properties.strokeWidth) / 2)} {...commonShapeProps} />
      ) : properties.shapeType === 'round-rectangle' ? (
        <rect x={strokeInset} y={strokeInset} width={width - strokeInset * 2} height={height - strokeInset * 2} rx={Math.min(width, height) * 0.18} {...commonShapeProps} />
      ) : properties.shapeType === 'square' || properties.shapeType === 'rectangle' ? (
        <rect x={strokeInset} y={strokeInset} width={width - strokeInset * 2} height={height - strokeInset * 2} {...commonShapeProps} />
      ) : properties.shapeType === 'hexagon' ? (
        <polygon points={`${width * 0.18},${strokeInset} ${width * 0.82},${strokeInset} ${width - strokeInset},${height / 2} ${width * 0.82},${height - strokeInset} ${width * 0.18},${height - strokeInset} ${strokeInset},${height / 2}`} {...commonShapeProps} />
      ) : properties.shapeType === 'diamond' ? (
        <polygon points={`${width / 2},${strokeInset} ${width - strokeInset},${height / 2} ${width / 2},${height - strokeInset} ${strokeInset},${height / 2}`} {...commonShapeProps} />
      ) : properties.shapeType === 'document' ? (
        <polygon points={`${strokeInset},${strokeInset} ${width * 0.82},${strokeInset} ${width - strokeInset},${height * 0.18} ${width - strokeInset},${height - strokeInset} ${strokeInset},${height - strokeInset}`} {...commonShapeProps} />
      ) : properties.shapeType === 'cylinder' ? (
        <>
          <path d={`M ${strokeInset} ${height * 0.18} V ${height * 0.82} C ${strokeInset} ${height * 0.96}, ${width - strokeInset} ${height * 0.96}, ${width - strokeInset} ${height * 0.82} V ${height * 0.18}`} {...commonShapeProps} />
          <ellipse cx={width / 2} cy={height * 0.18} rx={(width - properties.strokeWidth) / 2} ry={height * 0.12} {...commonShapeProps} />
          <path d={`M ${strokeInset} ${height * 0.82} C ${strokeInset} ${height * 0.96}, ${width - strokeInset} ${height * 0.96}, ${width - strokeInset} ${height * 0.82}`} fill="none" stroke={properties.stroke} strokeWidth={properties.strokeWidth} vectorEffect="non-scaling-stroke" />
        </>
      ) : properties.shapeType === 'triangle' ? (
        <polygon points={`${width / 2},${strokeInset} ${width - strokeInset},${height - strokeInset} ${strokeInset},${height - strokeInset}`} {...commonShapeProps} />
      ) : properties.shapeType === 'parallelogram' ? (
        <polygon points={`${width * 0.22},${strokeInset} ${width - strokeInset},${strokeInset} ${width * 0.78},${height - strokeInset} ${strokeInset},${height - strokeInset}`} {...commonShapeProps} />
      ) : properties.shapeType === 'plus' ? (
        <polygon points={`${width * 0.38},${strokeInset} ${width * 0.62},${strokeInset} ${width * 0.62},${height * 0.38} ${width - strokeInset},${height * 0.38} ${width - strokeInset},${height * 0.62} ${width * 0.62},${height * 0.62} ${width * 0.62},${height - strokeInset} ${width * 0.38},${height - strokeInset} ${width * 0.38},${height * 0.62} ${strokeInset},${height * 0.62} ${strokeInset},${height * 0.38} ${width * 0.38},${height * 0.38}`} {...commonShapeProps} />
      ) : (
        <rect x={strokeInset} y={strokeInset} width={width - strokeInset * 2} height={height - strokeInset * 2} {...commonShapeProps} />
      )}
    </svg>
  )
}

function ShapeNode({ data }: any) {
  const properties = normalizeFloorPlanProperties(data.properties)
  const handleStyle = { opacity: 0, width: 16, height: 16 }
  const selectedClass = data.isActive ? 'ring-2 ring-blue-500 ring-offset-2' : ''
  const isFreehand = properties.kind === 'freehand'

  return (
    <div className={selectedClass} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <NodeResizer
        isVisible={Boolean(data.isActive && !data.locked && !data.isReadOnly)}
        minWidth={24}
        minHeight={24}
        handleStyle={{ width: 9, height: 9, borderRadius: 2, borderColor: '#2563eb', backgroundColor: '#fff' }}
        lineStyle={{ borderColor: '#2563eb' }}
        onResizeEnd={(_event, params) => data.onShapeResize?.(data.id, params)}
      />
      <Handle id="target-top" type="target" position={Position.Top} style={{ ...handleStyle, top: -8, left: '50%', transform: 'translateX(-50%)' }} />
      <Handle id="source-top" type="source" position={Position.Top} style={{ ...handleStyle, top: -8, left: '50%', transform: 'translateX(-50%)' }} />
      <Handle id="target-right" type="target" position={Position.Right} style={{ ...handleStyle, right: -8, left: 'auto', top: '50%', transform: 'translateY(-50%)' }} />
      <Handle id="source-right" type="source" position={Position.Right} style={{ ...handleStyle, right: -8, left: 'auto', top: '50%', transform: 'translateY(-50%)' }} />
      <Handle id="target-bottom" type="target" position={Position.Bottom} style={{ ...handleStyle, bottom: -8, top: 'auto', left: '50%', transform: 'translateX(-50%)' }} />
      <Handle id="source-bottom" type="source" position={Position.Bottom} style={{ ...handleStyle, bottom: -8, top: 'auto', left: '50%', transform: 'translateX(-50%)' }} />
      <Handle id="target-left" type="target" position={Position.Left} style={{ ...handleStyle, left: -8, top: '50%', transform: 'translateY(-50%)' }} />
      <Handle id="source-left" type="source" position={Position.Left} style={{ ...handleStyle, left: -8, top: '50%', transform: 'translateY(-50%)' }} />
      {isFreehand ? (
        <FreehandSvg properties={properties} />
      ) : (
        <>
          <ShapeSvg properties={properties} />
          <div
            className="nodrag nowheel absolute inset-0 flex items-center justify-center px-2 text-center text-xs font-semibold text-gray-900 outline-none cursor-text"
            contentEditable={Boolean(data.isActive && !data.locked && !data.isReadOnly)}
            suppressContentEditableWarning
            onPointerDown={(event) => {
              if (data.isActive && !data.locked && !data.isReadOnly) event.stopPropagation()
            }}
            onBlur={(event) => {
              const label = event.currentTarget.textContent?.trim() || properties.label
              if (label !== properties.label) data.onShapeStyleChange?.(data.id, { label })
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                event.currentTarget.blur()
              }
            }}
          >
            {properties.label}
          </div>
        </>
      )}
    </div>
  )
}

function mapConnectionsToEdges(connections: any[], nodes: Node[]): EditableEdge[] {
  return connections.map((conn) => {
    const sourceNode = nodes.find((n) => n.data?.device_instance_id === conn.from_instance_id)
    const targetNode = nodes.find((n) => n.data?.device_instance_id === conn.to_instance_id)
    
    const sourceNodeId = sourceNode ? sourceNode.id : `instance-${conn.from_instance_id}`
    const targetNodeId = targetNode ? targetNode.id : `instance-${conn.to_instance_id}`
    
    const sourceHandle = conn.from_interface_id ? `${conn.from_interface_id}-source` : undefined
    const targetHandle = conn.to_interface_id ? `${conn.to_interface_id}-target` : undefined

    let wireProps: any = {}
    if (conn.wire_properties) {
      try {
        wireProps = typeof conn.wire_properties === 'string'
          ? JSON.parse(conn.wire_properties)
          : conn.wire_properties
      } catch (e) {
        // ignore
      }
    }

    let pathPoints: any[] = []
    if (conn.path_points) {
      try {
        const parsed = typeof conn.path_points === 'string'
          ? JSON.parse(conn.path_points)
          : conn.path_points
        if (Array.isArray(parsed)) {
          pathPoints = parsed.map((pt: any) => ({
            x: pt.x,
            y: pt.y,
            id: pt.id || window.crypto.randomUUID(),
            active: pt.active !== false,
            prev: pt.prev,
          }))
        }
      } catch (e) {
        // ignore
      }
    }

    const strokeColor = wireProps.color || '#0375ff'
    const strokeWidth = Number(wireProps.strokeWidth) || DEFAULT_EDGE_STROKE_WIDTH
    const preset = LINE_STYLE_OPTIONS.find((opt) => opt.value === wireProps.lineStyle)
    const strokeDasharray = wireProps.lineStyle === 'custom' && wireProps.dashNumbers 
      ? `${wireProps.dashNumbers.dash} ${wireProps.dashNumbers.gap}` 
      : (preset?.dasharray || undefined)

    return {
      id: `connection-${conn.id}`,
      type: 'editable-edge',
      source: sourceNodeId,
      target: targetNodeId,
      sourceHandle,
      targetHandle,
      style: {
        stroke: strokeColor,
        strokeWidth,
        strokeDasharray,
      },
      data: {
        connectionId: Number(conn.id),
        algorithm: wireProps.algorithm || Algorithm.Step,
        points: pathPoints,
        edgeLabels: wireProps.labels || [],
        layerId: wireProps.layerId || undefined,
        properties: conn,
        dbValues: {
          points: pathPoints,
          algorithm: wireProps.algorithm || Algorithm.Step,
          color: strokeColor,
          strokeWidth,
          strokeDasharray,
          edgeLabels: wireProps.labels || [],
          layerId: wireProps.layerId || undefined,
        }
      } as any,
    } as EditableEdge
  })
}

const nodeTypes = {
  device: DeviceNode,
  cable: CableNode,
  shape: ShapeNode,
}

const edgeTypes = {
  'editable-edge': EditableEdgeComponent,
}

interface NetworkSchemaEditorProps {
  projectId: string
  schemaId: string
  electricalPanelId?: number | null
}

function InnerNetworkSchemaEditor({ projectId, schemaId, electricalPanelId }: NetworkSchemaEditorProps) {
  const params = useParams<{ projectId: string; schemaId: string }>()
  const resolvedProjectId = Number(projectId || params.projectId)
  const rawSchemaParam = schemaId ?? params.schemaId
  const resolvedSchemaId = typeof rawSchemaParam === 'string' ? parseInt(rawSchemaParam, 10) : Number(rawSchemaParam)
  const { user: currentUser } = useAuth()
  
  // ============= PERMISSIONS CHECK =============
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, resolvedProjectId],
    queryFn: async () => {
      if (!currentUser?.id || !resolvedProjectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(resolvedProjectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!resolvedProjectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  const getEffectivePermission = useCallback((
    resourceType: 'electrical_panel' | 'schema',
    resourceId: number | null,
  ): string | null => {
    if (currentUser?.role === 'system_admin') {
      return 'manage'
    }

    if (!resolvedProjectId || !currentUser?.id) return null

    const explicit = allPermissions.find(
      (p) =>
        p.project_id === Number(resolvedProjectId) &&
        p.resource_type === resourceType &&
        p.resource_id === (resourceId ? String(resourceId) : null) &&
        p.permission !== 'deny',
    )
    if (explicit) {
      return explicit.permission
    }

    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(resolvedProjectId),
    )
    if (access && access.role_in_project) {
      const roleMap: Record<string, string> = {
        viewer: 'view',
        editor: 'edit',
        project_admin: 'manage',
      }
      return roleMap[access.role_in_project] || null
    }

    return null
  }, [currentUser, resolvedProjectId, allPermissions, projectAccessData])

  const isReadOnly = useMemo(() => {
    if (currentUser?.role === 'system_admin') return false
    if (!resolvedSchemaId) return false
    
    const perm = getEffectivePermission('schema', resolvedSchemaId)
    const result = perm === 'view'
    if (result) {
      console.log('🔒 Read-only mode enabled for floor plan schema:', resolvedSchemaId, 'permission:', perm, 'user:', currentUser?.email)
    }
    return result
  }, [currentUser, resolvedSchemaId, getEffectivePermission])
  // =============================================

  
  const queryClient = useQueryClient()
  
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null)
  const [selectedShapeId, setSelectedShapeId] = useState<number | null>(null)
  const dragRef = useRef<any>(null)
  const [search, setSearch] = useState('')
  const [activeToolPanel, setActiveToolPanel] = useState<'library' | 'layers' | 'shapes' | null>(null)
  const [freehandDrawing, setFreehandDrawing] = useState(false)
  const [activeLayerId, setActiveLayerId] = useState<number | null>(null)
  const [showDeviceTypeSections, setShowDeviceTypeSections] = useState<Record<string, boolean>>({})
  const edgeReconnectSuccessful = useRef(true)
  const edgesLoadedRef = useRef(false)
  const edgeSaveTimerRef = useRef<number | null>(null)
  const reactFlowInstance = useReactFlow()
  const canPersistViewportRef = useRef(false)
  const viewportRestoreAttemptedRef = useRef(false)

  const schemaIdValid = Number.isFinite(resolvedSchemaId) && resolvedSchemaId > 0
  const schemaManager = useMemo(() => createSchemaManager('network', schemaIdValid ? resolvedSchemaId : 1, {
    projectId: resolvedProjectId,
    userId: currentUser?.id,
  }), [schemaIdValid, resolvedSchemaId, resolvedProjectId, currentUser?.id])

  const [hasStoredViewport] = useState(() => {
    if (!schemaIdValid || !currentUser?.id) return false
    const key = `schema-viewport-v2-user-${currentUser.id}-project-${resolvedProjectId}-network-${resolvedSchemaId}`
    const legacyKey = `schema-viewport-network-${resolvedSchemaId}`
    return !!(localStorage.getItem(key) || localStorage.getItem(legacyKey))
  })

  useEffect(() => {
    canPersistViewportRef.current = false
    viewportRestoreAttemptedRef.current = false
  }, [schemaManager])

  const handleViewportMove = useCallback((_: unknown, viewport: Viewport) => {
    if (!canPersistViewportRef.current || !currentUser?.id) return
    schemaManager.saveViewport(viewport)
  }, [schemaManager, currentUser?.id])

  const { data: floorPlanData, isLoading } = useQuery({
    queryKey: ['floor-plan', resolvedSchemaId],
    queryFn: () => getFloorPlan(resolvedSchemaId),
    enabled: schemaIdValid,
  })

  const previousEdgesRef = useRef<EditableEdge[]>([])

  // Load connections for network schema
  const { data: dbConnections = [], refetch: refetchConnections } = useQuery({
    queryKey: ['schemaConnections', resolvedSchemaId],
    queryFn: () => getConnections(resolvedSchemaId),
    enabled: schemaIdValid,
    staleTime: 5 * 60 * 1000,
  })

  // Create connection mutation
  const createConnectionMutation = useMutation({
    mutationFn: async (connectionData: {
      schema_id: number
      from_instance_id: number
      from_interface_id: number
      to_instance_id: number
      to_interface_id: number
      wire_type: string
      wire_properties?: Record<string, any>
    }) => {
      return createConnection(connectionData)
    },
    onSuccess: () => {
      refetchConnections()
    },
    onError: (error) => {
      console.error('Failed to create connection:', error)
    }
  })

  // Delete connection mutation
  const deleteConnectionMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      await deleteConnection(connectionId)
    },
    onSuccess: () => {
      refetchConnections()
    },
    onError: (error) => {
      console.error('Failed to delete connection:', error)
    }
  })

  // Update connection mutation
  const updateConnectionMutation = useMutation({
    mutationFn: async ({ connectionId, params }: { connectionId: number; params: any }) => {
      await updateConnection(connectionId, params)
    },
    onSuccess: () => {
      refetchConnections()
    },
    onError: (error) => {
      console.error('Failed to update connection:', error)
    }
  })

  useEffect(() => {
    if (!floorPlanData || !currentUser?.id || viewportRestoreAttemptedRef.current) return

    viewportRestoreAttemptedRef.current = true
    const viewport = schemaManager.restoreViewport()
    if (viewport) {
      window.setTimeout(() => {
        reactFlowInstance.setViewport(viewport, { duration: 0 })
      }, 0)
    }
    window.setTimeout(() => {
      canPersistViewportRef.current = true
    }, 0)
  }, [floorPlanData, currentUser?.id, schemaManager, reactFlowInstance])

  const layersList = floorPlanData?.layers ?? []
  const editLayerForPanel = useMemo(() => {
    if (!layersList.length) return null
    const cur = activeLayerId != null ? layersList.find((l) => l.id === activeLayerId) : null
    return cur ?? layersList[0]
  }, [layersList, activeLayerId])
  const currentLayer = useMemo(
    () => layerById(layersList, activeLayerId ?? layersList[0]?.id),
    [layersList, activeLayerId],
  )
  const currentLayerLocked = isLayerLocked(currentLayer)

  const { data: availableDevices = [] } = useQuery({
    queryKey: ['floor-plan-devices', resolvedProjectId, resolvedSchemaId],
    queryFn: () => getAvailableDevicesForSchemaType(resolvedProjectId, 'network', undefined, resolvedSchemaId),
    enabled: Number.isFinite(resolvedProjectId) && resolvedProjectId > 0 && schemaIdValid,
  })

  // Removed availableCables for network schema

  const addItemMutation = useMutation({
    mutationFn: (payload: {
      entity_id: number
      entity_type: 'device' | 'cable' | 'infrastructure'
      image_x: number
      image_y: number
      layer_id?: number
    }) => addFloorPlanItem({ schema_id: resolvedSchemaId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] })
      queryClient.invalidateQueries({ queryKey: ['floor-plan-devices'] })
      queryClient.invalidateQueries({ queryKey: ['floor-plan-cables'] })
    },
  })

  const addShapeMutation = useMutation({
    mutationFn: (payload: {
      image_x: number
      image_y: number
      layer_id?: number
      properties: FloorPlanNodeProperties
    }) => addFloorPlanShape({ schema_id: resolvedSchemaId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] })
    },
  })

  const updateItemMutation = useMutation({
    mutationFn: ({ id, ...payload }: {
      id: number
      image_x?: number
      image_y?: number
      layer_id?: number
      properties?: FloorPlanNodeProperties | Record<string, unknown> | null
    }) => updateFloorPlanItem(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['floor-plan'] }),
  })

  const saveShapeMutation = useMutation({
    mutationFn: ({ id, ...payload }: {
      id: number
      image_x?: number
      image_y?: number
      layer_id?: number
      properties?: FloorPlanNodeProperties | Record<string, unknown> | null
    }) => updateFloorPlanItem(id, payload),
    onError: () => queryClient.invalidateQueries({ queryKey: ['floor-plan', resolvedSchemaId] }),
  })

  const removeItemMutation = useMutation({
    mutationFn: (itemId: number) => removeFloorPlanItem(itemId),
    onSuccess: () => {
      setSelectedItemId(null)
      setSelectedShapeId(null)
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] })
      queryClient.invalidateQueries({ queryKey: ['floor-plan-devices'] })
      queryClient.invalidateQueries({ queryKey: ['floor-plan-cables'] })
    },
  })

  const updateItemLayerMutation = useMutation({
    mutationFn: ({ id, layer_id }: { id: number; layer_id: number }) => updateFloorPlanItem(id, { layer_id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['floor-plan'] }),
  })

  const createLayerMutation = useMutation({
    mutationFn: (payload: Partial<FloorPlanLayer> & { schema_id: number; name?: string }) => createFloorPlanLayer(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['floor-plan'] }),
  })

  const patchLayerInFloorPlanCache = useCallback(
    (layerId: number, patch: Partial<FloorPlanLayer>) => {
      queryClient.setQueryData(['floor-plan', resolvedSchemaId], (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const payload = old as { layers?: FloorPlanLayer[] }
        if (!Array.isArray(payload.layers)) return old
        return {
          ...payload,
          layers: payload.layers.map((layer) => (
            layer.id === layerId ? { ...layer, ...patch } : layer
          )),
        }
      })
    },
    [queryClient, resolvedSchemaId],
  )

  const updateLayerMutation = useMutation({
    mutationFn: ({ id, ...rest }: { id: number } & Partial<Omit<FloorPlanLayer, 'id' | 'schema_id'>>) =>
      updateFloorPlanLayer(id, rest),
    onMutate: ({ id, ...rest }) => {
      patchLayerInFloorPlanCache(id, rest)
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['floor-plan', resolvedSchemaId] }),
  })

  const deleteLayerMutation = useMutation({
    mutationFn: ({ id, targetId }: { id: number; targetId?: number }) => deleteFloorPlanLayer(id, targetId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['floor-plan'] }),
  })



  const patchShapeInFloorPlanCache = useCallback((shapeId: number, patch: Partial<FloorPlanShape>) => {
    queryClient.setQueryData(['floor-plan', resolvedSchemaId], (old: unknown) => {
      if (!old || typeof old !== 'object') return old
      const payload = old as FloorPlanPayload
      if (!Array.isArray(payload.shapes)) return old
      return {
        ...payload,
        shapes: payload.shapes.map((shape) => (
          shape.id === shapeId ? { ...shape, ...patch } : shape
        )),
      }
    })
  }, [queryClient, resolvedSchemaId])

  const updateShapeProperties = useCallback((shapeId: number, patch: Partial<FloorPlanShapeStyle> | Partial<FloorPlanFreehandStyle>) => {
    const shape = floorPlanData?.shapes?.find((item) => item.id === shapeId)
    if (!shape) return
    const currentProperties = normalizeFloorPlanProperties(shape.properties)
    const properties = {
      ...currentProperties,
      ...patch,
      kind: currentProperties.kind,
    } as FloorPlanNodeProperties
    patchShapeInFloorPlanCache(shapeId, { properties })
    saveShapeMutation.mutate({
      id: shapeId,
      properties,
    })
  }, [floorPlanData?.shapes, patchShapeInFloorPlanCache, saveShapeMutation.mutate])

  const resizeShapeNode = useCallback((shapeId: number, params: { x: number; y: number; width: number; height: number }) => {
    const shape = floorPlanData?.shapes?.find((item) => item.id === shapeId)
    if (!shape) return
    const width = Math.max(24, Math.round(params.width))
    const height = Math.max(24, Math.round(params.height))
    const image_x = params.x + width / 2
    const image_y = params.y + height / 2
    const currentProperties = normalizeFloorPlanProperties(shape.properties)
    const properties = {
      ...currentProperties,
      width,
      height,
      kind: currentProperties.kind,
    } as FloorPlanNodeProperties
    patchShapeInFloorPlanCache(shapeId, { image_x, image_y, properties })
    saveShapeMutation.mutate({
      id: shapeId,
      image_x,
      image_y,
      properties,
    })
  }, [floorPlanData?.shapes, patchShapeInFloorPlanCache, saveShapeMutation.mutate])

  // Image loading logic removed for network schema

  useEffect(() => {
    const layers = floorPlanData?.layers
    if (!schemaIdValid) return
    if (!layers?.length) {
      setActiveLayerId(null)
      return
    }
    let stored: string | null = null
    try {
      stored = localStorage.getItem(ACTIVE_LAYER_STORAGE_KEY(resolvedSchemaId))
    } catch {
      stored = null
    }
    const parsed = stored ? Number(stored) : NaN
    const valid = Number.isFinite(parsed) && layers.some((l) => l.id === parsed)
    setActiveLayerId(valid ? parsed : layers[0].id)
  }, [resolvedSchemaId, schemaIdValid, floorPlanData?.layers])

  useEffect(() => {
    if (activeLayerId == null || !schemaIdValid) return
    try {
      localStorage.setItem(ACTIVE_LAYER_STORAGE_KEY(resolvedSchemaId), String(activeLayerId))
    } catch {
      /* ignore */
    }
  }, [activeLayerId, resolvedSchemaId, schemaIdValid])

  useEffect(() => {
    if (currentLayerLocked) {
      setFreehandDrawing(false)
    }
  }, [currentLayerLocked])

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<EditableEdge>([])

  // Helper to map wire type from algorithm
  const getWireTypeFromAlgorithm = (algorithm?: Algorithm): string | undefined => {
    switch (algorithm) {
      case Algorithm.Step: return 'step';
      case Algorithm.Linear: return 'straight';
      case Algorithm.CatmullRom: return 'smoothstep';
      case Algorithm.BezierCatmullRom: return 'bezier';
      default: return undefined;
    }
  };

  // Sync edges from dbConnections and nodes
  useEffect(() => {
    if (!dbConnections || !nodes || nodes.length === 0) return
    const mappedEdges = mapConnectionsToEdges(dbConnections, nodes)

    setEdges((prevEdges) => {
      // Check if edges actually changed to avoid infinite loops
      if (prevEdges.length === mappedEdges.length && 
          prevEdges.every((e, i) => {
            const newE = mappedEdges[i];
            if (e.id !== newE.id) return false;
            if (e.source !== newE.source || e.target !== newE.target) return false;
            if (e.sourceHandle !== newE.sourceHandle || e.targetHandle !== newE.targetHandle) return false;
            if (e.style?.stroke !== newE.style?.stroke || 
                e.style?.strokeWidth !== newE.style?.strokeWidth || 
                e.style?.strokeDasharray !== newE.style?.strokeDasharray) return false;
            if (JSON.stringify(e.data?.points) !== JSON.stringify(newE.data?.points)) return false;
            if (e.data?.algorithm !== newE.data?.algorithm) return false;
            if (e.selected !== newE.selected) return false;
            if (JSON.stringify(e.data?.edgeLabels) !== JSON.stringify(newE.data?.edgeLabels)) return false;
            if (e.data?.layerId !== newE.data?.layerId) return false;
            return true;
          })) {
        return prevEdges;
      }
      
      const nextEdges = mappedEdges.map(newEdge => {
        const prevEdge = prevEdges.find(e => e.id === newEdge.id);
        if (prevEdge) {
          // Preserve local state like selection and points (if currently dragging)
          const shouldResetPoints = !newEdge.data?.points || (Array.isArray(newEdge.data.points) && newEdge.data.points.length === 0);
          return { 
            ...newEdge, 
            selected: prevEdge.selected,
            style: prevEdge.style, // preserve style changes during active session
            data: {
              ...newEdge.data,
              points: shouldResetPoints ? [] : (prevEdge.data?.points || newEdge.data?.points || [])
            }
          } as EditableEdge;
        }
        return newEdge;
      });
      previousEdgesRef.current = nextEdges;
      return nextEdges;
    });

    edgesLoadedRef.current = true
  }, [dbConnections, nodes])

  // Watch for local edge changes and save to server
  useEffect(() => {
    if (!edgesLoadedRef.current || !schemaIdValid) return

    if (edgeSaveTimerRef.current) {
      window.clearTimeout(edgeSaveTimerRef.current)
    }

    edgeSaveTimerRef.current = window.setTimeout(() => {
      edges.forEach((edge) => {
        const db = (edge.data as any)?.dbValues
        if (db && edge.data) {
          const pointsChanged = JSON.stringify(db.points) !== JSON.stringify(edge.data.points)
          const algorithmChanged = db.algorithm !== edge.data.algorithm
          const colorChanged = db.color !== edge.style?.stroke
          const strokeWidthChanged = db.strokeWidth !== edge.style?.strokeWidth
          const strokeDasharrayChanged = db.strokeDasharray !== edge.style?.strokeDasharray
          const labelsChanged = JSON.stringify(db.edgeLabels) !== JSON.stringify(edge.data.edgeLabels)
          const layerIdChanged = db.layerId !== edge.data.layerId

          if (pointsChanged || algorithmChanged || colorChanged || strokeWidthChanged || strokeDasharrayChanged || labelsChanged || layerIdChanged) {
            const connectionId = Number(edge.data.connectionId)
            if (connectionId) {
              const pathPoints = edge.data.points?.map((p: any) => ({ x: p.x, y: p.y, type: 'waypoint' })) || []
              
              // Get dash numbers from current style
              const dashNumbers = getDashNumbers(edge.style?.strokeDasharray)
              const currentLineStyle = getLineStylePreset(edge.style?.strokeDasharray)
              
              const rawWireProps = (edge.data as any).properties?.wire_properties
              let parsedWireProps = {}
              if (rawWireProps) {
                try {
                  parsedWireProps = typeof rawWireProps === 'string' ? JSON.parse(rawWireProps) : rawWireProps
                } catch (e) {
                  console.warn('Failed to parse wire_properties for edge', edge.id, e)
                }
              }

              const wireProperties: any = {
                ...parsedWireProps,
                algorithm: edge.data.algorithm,
                color: edge.style?.stroke,
                strokeWidth: edge.style?.strokeWidth,
                lineStyle: currentLineStyle,
                dashNumbers,
                labels: edge.data.edgeLabels || [],
                layerId: edge.data.layerId,
              }
              
              const edgeType = getWireTypeFromAlgorithm(edge.data.algorithm as Algorithm | undefined)
              if (edgeType) {
                wireProperties.type = edgeType
              }

              updateConnectionMutation.mutate({
                connectionId,
                params: {
                  path_points: pathPoints,
                  is_manual_route: true,
                  wire_properties: wireProperties,
                },
              })
            }
          }
        }
      })
      previousEdgesRef.current = edges
    }, FLOOR_PLAN_EDGE_SAVE_DELAY_MS)

    return () => {
      if (edgeSaveTimerRef.current) {
        window.clearTimeout(edgeSaveTimerRef.current)
      }
    }
  }, [edges, resolvedSchemaId, schemaIdValid])

  useEffect(() => {
    if (!floorPlanData) return

    const layers = floorPlanData.layers ?? []
    const visibleItems = (floorPlanData.items ?? []).filter((item) => {
      const lid = resolveItemLayerId(item, layers)
      return isLayerVisible(layerById(layers, lid))
    })
    const visibleShapes = (floorPlanData.shapes ?? []).filter((shape) => {
      const lid = resolveItemLayerId(shape as any, layers)
      return isLayerVisible(layerById(layers, lid))
    })

    const newNodes: Node[] = []

    // No background image node for network schema

    const itemNodes = visibleItems.map((item) => {
      const isActive = item.id === selectedItemId
      const markerSize = Number(item.marker_size || 10)
      const type = item.entity_type === 'cable' ? 'cable' : 'device'
      const lid = resolveItemLayerId(item, layers)
      const layer = layerById(layers, lid)
      const locked = isLayerLocked(layer)
      const scale = layer ? Number(layer.marker_scale) || 1 : 1
      const layerStyle = layer
        ? {
            svgMarkup: layer.svg_markup || undefined,
            svgColor: layer.svg_color || (type === 'cable' ? '#16a34a' : '#2563eb'),
            markerScale: scale,
            labelFontSize: Number(layer.label_font_size) || (type === 'cable' ? 11 : 12),
            labelBgColor: layer.label_bg_color || 'rgba(255,255,255,0.9)',
          }
        : undefined

      let x = item.image_x
      let y = item.image_y

      if (type === 'device') {
        const outer = Math.max(markerSize, 40) * scale
        x -= outer / 2
        y -= outer / 2
      } else if (type === 'cable') {
        const w = Math.max(markerSize * 8, 80) * scale
        const h = Math.max(markerSize * 3, 30) * scale
        x -= w / 2
        y -= h / 2
      }

      const zBase = layer ? Number(layer.sort_order) * 10 + 2 : 2

      return {
        id: `item-${item.id}`,
        type,
        position: { x, y },
        data: {
          ...item,
          isActive,
          markerSize,
          layerStyle,
          label: item.position_label || `ID ${item.device_instance_id}`,
          networkType: (floorPlanData?.schema as any)?.network_type,
        },
        draggable: !locked && !isReadOnly,
        selectable: !locked,
        zIndex: isActive ? zBase + 100 : zBase,
      }
    })

    newNodes.push(...itemNodes)
    const shapeNodes = visibleShapes.map((shape) => {
      const properties = normalizeFloorPlanProperties(shape.properties)
      const lid = resolveItemLayerId(shape as any, layers)
      const layer = layerById(layers, lid)
      const locked = isLayerLocked(layer)
      const zBase = layer ? Number(layer.sort_order) * 10 + 3 : 3

      return {
        id: `shape-${shape.id}`,
        type: 'shape',
        position: {
          x: shape.image_x - properties.width / 2,
          y: shape.image_y - properties.height / 2,
        },
        data: {
          ...shape,
          properties,
          isActive: shape.id === selectedShapeId,
          locked,
          isReadOnly,
          onShapeResize: resizeShapeNode,
          onShapeStyleChange: updateShapeProperties,
        },
        draggable: !locked && (!isReadOnly || properties.kind === 'freehand'),
        selectable: !locked,
        style: { width: properties.width, height: properties.height },
        zIndex: shape.id === selectedShapeId ? zBase + 100 : zBase,
      }
    })

    newNodes.push(...shapeNodes)
    setNodes(newNodes)
  }, [floorPlanData, resizeShapeNode, selectedItemId, selectedShapeId, setNodes, updateShapeProperties, isReadOnly])

  const onNodeDragStop = useCallback((_event: React.MouseEvent, node: Node) => {
    if (isReadOnly) {
      const isFreehand = node.type === 'shape' && (node.data?.properties as any)?.kind === 'freehand'
      if (!isFreehand) return
    }
    if (node.type !== 'device' && node.type !== 'cable' && node.type !== 'shape') return
    const markerSize = (node.data.markerSize as number) || 10
    const layerSt = node.data.layerStyle as { markerScale?: number } | undefined
    const scale =
      typeof layerSt?.markerScale === 'number' ? layerSt.markerScale : Number(layerSt?.markerScale) || 1

    let image_x = node.position.x
    let image_y = node.position.y

    if (node.type === 'shape') {
      const properties = normalizeFloorPlanProperties(node.data.properties)
      image_x += (node.width ?? properties.width) / 2
      image_y += (node.height ?? properties.height) / 2
    } else if (node.type === 'device') {
      const outer = Math.max(markerSize, 40) * scale
      image_x += outer / 2
      image_y += outer / 2
    } else if (node.type === 'cable') {
      const w = Math.max(markerSize * 8, 80) * scale
      const h = Math.max(markerSize * 3, 30) * scale
      image_x += w / 2
      image_y += h / 2
    }

    updateItemMutation.mutate({
      id: node.data.id as number,
      image_x,
      image_y,
    })
  }, [updateItemMutation])

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    if (node.type === 'device' || node.type === 'cable') {
      setSelectedItemId(node.data.id as number)
      setSelectedShapeId(null)
      setEdges((edges) => edges.map((edge) => ({ ...edge, selected: false })))
    } else if (node.type === 'shape') {
      setSelectedShapeId(node.data.id as number)
      setSelectedItemId(null)
      setEdges((edges) => edges.map((edge) => ({ ...edge, selected: false })))
    }
  }, [setEdges])

  const onPaneClick = useCallback(() => {
    setSelectedItemId(null)
    setSelectedShapeId(null)
    setEdges((edges) => edges.map((edge) => ({ ...edge, selected: false })))
  }, [setEdges])

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: EditableEdge) => {
      const layers = floorPlanData?.layers ?? []
      const elid = edge.data?.layerId ?? layers[0]?.id
      if (isLayerLocked(layerById(layers, elid))) {
        return
      }
      event.stopPropagation()
      setSelectedItemId(null)
      setSelectedShapeId(null)
      setEdges((eds) =>
        eds.map((currentEdge) => ({
          ...currentEdge,
          selected: currentEdge.id === edge.id,
        })),
      )
    },
    [setEdges, floorPlanData?.layers],
  )

  const onConnect = useCallback(
    (connection: any) => {
      if (isReadOnly) return
      
      const { source, target, sourceHandle, targetHandle } = connection
      if (!source || !target || !sourceHandle || !targetHandle) return

      // Find nodes to get device_instance_ids
      const sourceNode = nodes.find((n) => n.id === source)
      const targetNode = nodes.find((n) => n.id === target)
      if (!sourceNode || !targetNode) return

      const fromInstanceId = sourceNode.data?.device_instance_id as number
      const toInstanceId = targetNode.data?.device_instance_id as number
      if (!fromInstanceId || !toInstanceId) return

      // Handles format: `${interface_id}-source` / `${interface_id}-target`
      const fromInterfaceId = parseInt(sourceHandle.split('-')[0], 10)
      const toInterfaceId = parseInt(targetHandle.split('-')[0], 10)
      if (isNaN(fromInterfaceId) || isNaN(toInterfaceId)) return

      const layers = floorPlanData?.layers ?? []
      const currentLayer = layerById(layers, activeLayerId ?? layers[0]?.id)
      if (currentLayer && isLayerLocked(currentLayer)) {
        return
      }

      const lineAlgo = currentLayer
        ? algorithmFromLayerString(currentLayer.line_algorithm)
        : DEFAULT_ALGORITHM

      // Trigger create connection mutation
      createConnectionMutation.mutate({
        schema_id: resolvedSchemaId,
        from_instance_id: fromInstanceId,
        from_interface_id: fromInterfaceId,
        to_instance_id: toInstanceId,
        to_interface_id: toInterfaceId,
        wire_type: 'signal',
        wire_properties: {
          algorithm: lineAlgo,
          layerId: currentLayer?.id ?? activeLayerId ?? layers[0]?.id,
        }
      })
    },
    [nodes, floorPlanData?.layers, activeLayerId, resolvedSchemaId, createConnectionMutation, isReadOnly],
  )

  const onReconnectStart = useCallback(() => {
    if (isReadOnly) return
    edgeReconnectSuccessful.current = false
  }, [isReadOnly])

  const onReconnect = useCallback((oldEdge: EditableEdge, newConnection: any) => {
    if (isReadOnly) return
    edgeReconnectSuccessful.current = true
    setEdges((edges) => reconnectEdge(oldEdge, newConnection, edges) as EditableEdge[])
  }, [setEdges, isReadOnly])

  const onReconnectEnd = useCallback(() => {
    if (isReadOnly) return
    edgeReconnectSuccessful.current = true
  }, [isReadOnly])

  const onEdgesDelete = useCallback(
    (edgesToDelete: any[]) => {
      if (isReadOnly) return
      edgesToDelete.forEach((edge) => {
        const connectionId = edge.data?.connectionId
        if (connectionId) {
          deleteConnectionMutation.mutate(Number(connectionId))
        }
      })
    },
    [deleteConnectionMutation, isReadOnly]
  )

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (isReadOnly) return

    const raw = event.dataTransfer.getData(DEVICE_DRAG_TYPE)
    console.log('Dropped raw data:', raw, 'dragRef.current:', dragRef.current)
    
    let payload = null;
    
    if (raw) {
      try {
        payload = JSON.parse(raw)
      } catch (error) {
        console.error('floor plan drop parse error', error)
      }
    } else if (dragRef.current) {
      payload = dragRef.current
    }

    if (!payload) return

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    })

    try {
      console.log('Parsed drop payload:', payload)
      if (payload.kind === 'shape') {
        addShapeMutation.mutate({
          image_x: position.x,
          image_y: position.y,
          ...(activeLayerId != null ? { layer_id: activeLayerId } : {}),
          properties: createShapeStyle(payload.preset as ShapePreset),
        })
        return
      }
      const entityId = payload.entity_id || payload.device_instance_id
      const entityType = payload.entity_type || 'device'
      if (!entityId) return
      addItemMutation.mutate({
        entity_id: Number(entityId),
        entity_type: entityType as 'device' | 'cable' | 'infrastructure',
        image_x: position.x,
        image_y: position.y,
        ...(activeLayerId != null ? { layer_id: activeLayerId } : {}),
      })
    } catch (error) {
      console.error('floor plan drop mutation error', error)
    } finally {
      dragRef.current = null;
    }
  }, [addItemMutation, addShapeMutation, activeLayerId, reactFlowInstance])

  const handleFreehandComplete = useCallback((drawing: { position: { x: number; y: number }; properties: FloorPlanFreehandStyle }) => {
    if (currentLayerLocked) return

    addShapeMutation.mutate({
      image_x: drawing.position.x + drawing.properties.width / 2,
      image_y: drawing.position.y + drawing.properties.height / 2,
      ...(activeLayerId != null ? { layer_id: activeLayerId } : {}),
      properties: drawing.properties,
    })
  }, [activeLayerId, addShapeMutation, currentLayerLocked])

  const selectedItem = useMemo<SelectedDevice>(() => floorPlanData?.items?.find((item) => item.id === selectedItemId) ?? null, [floorPlanData?.items, selectedItemId])
  const selectedShape = useMemo<FloorPlanShape | null>(() => floorPlanData?.shapes?.find((shape) => shape.id === selectedShapeId) ?? null, [floorPlanData?.shapes, selectedShapeId])
  const selectedEdge = useMemo(() => edges.find((edge) => edge.selected) ?? null, [edges])

  const edgesForFlow = useMemo(() => {
    const layers = floorPlanData?.layers ?? []
    const items = floorPlanData?.items ?? []
    const shapes = floorPlanData?.shapes ?? []
    const planNodeLayerId = (nodeId: string) => {
      const match = /^(item|shape)-(\d+)$/.exec(nodeId)
      if (!match) return null
      const planNodeId = Number(match[2])
      const planNode = match[1] === 'item'
        ? items.find((i) => i.id === planNodeId)
        : shapes.find((shape) => shape.id === planNodeId)
      return planNode ? resolveItemLayerId(planNode as any, layers) : null
    }
    return edges.map((edge) => {
      const sourceLayerId = planNodeLayerId(edge.source)
      const targetLayerId = planNodeLayerId(edge.target)
      let hidden = false
      let locked = false
      if (sourceLayerId != null && targetLayerId != null) {
        const sourceLayer = layerById(layers, sourceLayerId)
        const targetLayer = layerById(layers, targetLayerId)
        const endOk = isLayerVisible(sourceLayer) && isLayerVisible(targetLayer)
        const elid = edge.data?.layerId ?? layers[0]?.id
        const lyr = layerById(layers, elid)
        locked = isLayerLocked(lyr)
        hidden = !endOk || !isLayerVisible(lyr)
      }
      return {
        ...edge,
        hidden,
        selectable: !locked && !hidden,
        focusable: !locked && !hidden,
      }
    })
  }, [edges, floorPlanData?.items, floorPlanData?.layers, floorPlanData?.shapes])
  const selectedLineStyle = getLineStylePreset(selectedEdge?.style?.strokeDasharray)
  const selectedDashNumbers = getDashNumbers(selectedEdge?.style?.strokeDasharray)
  const selectedLineColor = typeof selectedEdge?.style?.stroke === 'string'
    ? selectedEdge.style.stroke
    : COLORS[selectedEdge?.data?.algorithm ?? DEFAULT_ALGORITHM]
  const selectedLineWidth = Number(selectedEdge?.style?.strokeWidth ?? DEFAULT_EDGE_STROKE_WIDTH) || DEFAULT_EDGE_STROKE_WIDTH
  const selectedEdgeLabels = selectedEdge?.data?.edgeLabels ?? []
  const selectedEdgeLabelText = selectedEdgeLabels[0]?.text ?? ''

  const updateSelectedEdge = useCallback((update: (edge: EditableEdge) => EditableEdge) => {
    setEdges((eds) => eds.map((edge) => (edge.selected ? update(edge) : edge)))
  }, [setEdges])

  const setSelectedEdgeLayerId = useCallback(
    (layerId: number) => {
      updateSelectedEdge((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          points: edge.data?.points ?? [],
          layerId,
        },
      }))
    },
    [updateSelectedEdge],
  )

  const applySelectedEdgeLayerStyle = useCallback(() => {
    if (!selectedEdge) return
    const layers = floorPlanData?.layers ?? []
    const lid = selectedEdge.data?.layerId ?? layers[0]?.id
    const lyr = layerById(layers, lid)
    if (!lyr) return
    updateSelectedEdge((edge) => ({
      ...edge,
      data: {
        ...edge.data,
        points: edge.data?.points ?? [],
        algorithm: algorithmFromLayerString(lyr.line_algorithm),
      },
      style: {
        ...edge.style,
        stroke: lyr.line_color || undefined,
        strokeWidth: Number(lyr.line_stroke_width) || DEFAULT_EDGE_STROKE_WIDTH,
        strokeDasharray: lyr.line_stroke_dasharray || undefined,
      },
    }))
  }, [selectedEdge, floorPlanData?.layers, updateSelectedEdge])

  const updateSelectedEdgeLabels = useCallback((labels: EdgeLabelData[]) => {
    updateSelectedEdge((edge) => ({
      ...edge,
      data: {
        ...edge.data,
        points: edge.data?.points ?? [],
        edgeLabels: labels,
      },
    }))
  }, [updateSelectedEdge])

  const createFirstSelectedEdgeLabel = useCallback(() => {
    if (selectedEdgeLabels.length > 0) return
    updateSelectedEdgeLabels([createEdgeLabel('Цепь', 0)])
  }, [selectedEdgeLabels.length, updateSelectedEdgeLabels])

  const resetSelectedEdgeRoute = useCallback(() => {
    updateSelectedEdge((edge) => ({
      ...edge,
      data: {
        ...edge.data,
        points: [],
      },
    }))
  }, [updateSelectedEdge])

  const resetSelectedEdgeVisual = useCallback(() => {
    updateSelectedEdge((edge) => ({
      ...edge,
      data: {
        ...edge.data,
        points: edge.data?.points ?? [],
        algorithm: DEFAULT_ALGORITHM,
      },
      style: undefined,
    }))
  }, [updateSelectedEdge])

  const deleteSelectedEdge = useCallback(() => {
    if (isReadOnly) return
    const selectedEdges = edges.filter((edge) => edge.selected)
    selectedEdges.forEach((edge) => {
      const connectionId = edge.data?.connectionId
      if (connectionId) {
        deleteConnectionMutation.mutate(Number(connectionId))
      }
    })
    setEdges((edges) => edges.filter((edge) => !edge.selected))
  }, [edges, deleteConnectionMutation, setEdges, isReadOnly])

  const closeSelectedEdgePanel = useCallback(() => {
    setEdges((edges) => edges.map((edge) => ({ ...edge, selected: false })))
  }, [setEdges])

  const filteredDevices = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return availableDevices
    return availableDevices.filter((device: any) => {
      const label = String(device.position_label ?? '').toLowerCase()
      const spec = String(device.spec_name ?? '').toLowerCase()
      const description = String(device.description ?? '').toLowerCase()
      return label.includes(q) || spec.includes(q) || description.includes(q)
    })
  }, [availableDevices, search])

  // Removed filteredCables for network schema

  const groupedDevices = useMemo(() => {
    const groups: Record<string, any[]> = {};
    filteredDevices.forEach((device: any) => {
      const type = device.device_type_code || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(device);
    });
    return groups;
  }, [filteredDevices]);

  const getDeviceTypeDisplayName = (typeCode: string) => {
    const names: Record<string, string> = {
      breaker: 'Автоматы',
      relay: 'Реле',
      dimmer: 'Диммеры',
      terminal: 'Клеммники',
      power_supply: 'Блоки питания',
      controller: 'Контроллеры',
      other: 'Другое'
    };
    return names[typeCode] || typeCode;
  };

  const selectedShapeProperties = selectedShape ? normalizeFloorPlanProperties(selectedShape.properties) : null
  const selectedShapeStyle = selectedShapeProperties?.kind === 'shape' ? selectedShapeProperties : null
  const selectedFreehandStyle = selectedShapeProperties?.kind === 'freehand' ? selectedShapeProperties : null
  const updateSelectedShapeStyle = useCallback((patch: Partial<FloorPlanShapeStyle>) => {
    if (!selectedShape) return
    updateShapeProperties(selectedShape.id, patch)
  }, [selectedShape, updateShapeProperties])
  const updateSelectedFreehandStyle = useCallback((patch: Partial<FloorPlanFreehandStyle>) => {
    if (!selectedShape) return
    updateShapeProperties(selectedShape.id, patch)
  }, [selectedShape, updateShapeProperties])

  if (!schemaIdValid) {
    return (
      <div className="h-screen flex items-center justify-center p-6 text-center text-red-700">
        Некорректный адрес схемы: в URL должен быть числовой ID схемы, например <code className="bg-red-50 px-1 rounded">/projects/…/schemas/123</code>, а не имя типа.
      </div>
    )
  }

  if (isLoading) {
    return <div className="h-screen flex items-center justify-center text-gray-600">Загрузка плана...</div>
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <SchemaEditorHeader
        projectId={resolvedProjectId}
        schemaId={resolvedSchemaId}
        electricalPanelId={floorPlanData?.electrical_panel?.id ?? electricalPanelId ?? undefined}
        schemaName={floorPlanData?.schema?.name}
        panelName={floorPlanData?.electrical_panel?.name}
      />

      <div className="flex-1 flex overflow-hidden">
      <aside className="w-16 border-r border-gray-700 bg-gray-800 flex flex-col items-center py-4 gap-3 z-20">
        <button
          type="button"
          onClick={() => setActiveToolPanel(activeToolPanel === 'library' ? null : 'library')}
          disabled={isReadOnly}
          className={`w-12 h-12 rounded-lg flex items-center justify-center text-xl text-white transition-colors ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-600' : activeToolPanel === 'library' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'}`}
          title={isReadOnly ? 'Режим только просмотр' : 'Библиотека устройств'}
        >
          📚
        </button>
        <button
          type="button"
          onClick={() => setActiveToolPanel(activeToolPanel === 'layers' ? null : 'layers')}
          disabled={isReadOnly}
          className={`w-12 h-12 rounded-lg flex items-center justify-center text-xl text-white transition-colors ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-600' : activeToolPanel === 'layers' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'}`}
          title={isReadOnly ? 'Режим только просмотр' : 'Слои схемы'}
        >
          📑
        </button>
        <button
          type="button"
          onClick={() => setActiveToolPanel(activeToolPanel === 'shapes' ? null : 'shapes')}
          disabled={isReadOnly}
          className={`w-12 h-12 rounded-lg flex items-center justify-center text-xl text-white transition-colors ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-600' : activeToolPanel === 'shapes' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'}`}
          title={isReadOnly ? 'Режим только просмотр' : 'Фигуры'}
        >
          ◼
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveToolPanel(null)
            setSelectedItemId(null)
            setSelectedShapeId(null)
            setEdges((edges) => edges.map((edge) => ({ ...edge, selected: false })))
            setFreehandDrawing((value) => !value)
          }}
          disabled={currentLayerLocked}
          className={`w-12 h-12 rounded-lg flex items-center justify-center text-xl text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${freehandDrawing ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600 hover:bg-gray-500'}`}
          title={currentLayerLocked ? 'Текущий слой заблокирован' : 'Рисовать от руки'}
        >
          ✎
        </button>
      </aside>

      {activeToolPanel === 'layers' && (
        <aside className="w-80 border-r border-gray-200 bg-white flex flex-col z-10 relative max-h-[calc(100vh-2.5rem)]">
          <div className="p-4 border-b border-gray-200 shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Слои</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Текущий слой — для новых элементов и линий. Линии наследуют тип/цвет/толщину слоя при создании.
                </p>
              </div>
              <button type="button" onClick={() => setActiveToolPanel(null)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <button
              type="button"
              onClick={() =>
                createLayerMutation.mutate({
                  schema_id: resolvedSchemaId,
                  name: `Слой ${(layersList.length || 0) + 1}`,
                })
              }
              disabled={createLayerMutation.isPending}
              className="mt-3 w-full px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-60"
            >
              + Добавить слой
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {layersList.length === 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                Слоёв нет или таблица не создана. Выполните на сервере SQL{' '}
                <code className="text-[10px]">migrate_floor_plan_layers.sql</code>.
              </p>
            )}
            {layersList.map((layer) => (
              <div
                key={layer.id}
                className={`rounded-lg border p-2 space-y-1 ${activeLayerId === layer.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}
              >
                <div className="flex items-center gap-2">
                  <input
                    key={`${layer.id}-${layer.updated_at ?? layer.name}`}
                    className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm"
                    defaultValue={layer.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim()
                      if (v && v !== layer.name) updateLayerMutation.mutate({ id: layer.id, name: v })
                    }}
                  />
                  <button
                    type="button"
                    title="Сделать текущим (новые элементы и линии)"
                    onClick={() => setActiveLayerId(layer.id)}
                    className={`shrink-0 px-2 py-1 rounded text-xs ${activeLayerId === layer.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    Текущий
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 text-xs items-center">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Number(layer.visible) !== 0}
                      onChange={(e) => updateLayerMutation.mutate({ id: layer.id, visible: e.target.checked ? 1 : 0 })}
                    />
                    Видим
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Number(layer.locked) !== 0}
                      onChange={(e) => updateLayerMutation.mutate({ id: layer.id, locked: e.target.checked ? 1 : 0 })}
                    />
                    Блок
                  </label>
                  <button
                    type="button"
                    className="text-red-600 hover:text-red-800 ml-auto"
                    disabled={layersList.length <= 1 || deleteLayerMutation.isPending}
                    onClick={() => {
                      if (!window.confirm('Удалить слой? Элементы и линии будут переназначены на другой слой.')) return
                      deleteLayerMutation.mutate({ id: layer.id })
                    }}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            ))}

            {editLayerForPanel && (
              <div className="border-t border-gray-200 pt-3 space-y-2 mt-2">
                <p className="text-xs font-semibold text-gray-800">Настройки слоя «{editLayerForPanel.name}»</p>
                <label className="block text-xs text-gray-600">
                  SVG (файл)
                  <input
                    type="file"
                    accept=".svg,image/svg+xml"
                    className="block w-full text-[11px] mt-0.5"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]
                      if (!f) return
                      const t = await f.text()
                      updateLayerMutation.mutate({ id: editLayerForPanel.id, svg_markup: t })
                      e.target.value = ''
                    }}
                  />
                </label>
                <ColorPickerField
                  key={`svgc-${editLayerForPanel.id}`}
                  label="Цвет SVG"
                  value={editLayerForPanel.svg_color}
                  fallback="rgba(37, 99, 235, 1)"
                  onCommit={(value) => updateLayerMutation.mutate({ id: editLayerForPanel.id, svg_color: value })}
                />
                <label className="block text-xs">
                  Толщина линии SVG
                  <input
                    type="number"
                    step={0.25}
                    min={0.25}
                    className="mt-0.5 w-full border rounded px-2 py-1 text-sm"
                    defaultValue={Number(editLayerForPanel.svg_stroke_width) || 2}
                    key={`sw-${editLayerForPanel.id}-${editLayerForPanel.updated_at}`}
                    onBlur={(e) =>
                      updateLayerMutation.mutate({ id: editLayerForPanel.id, svg_stroke_width: Number(e.target.value) || 2 })
                    }
                  />
                </label>
                <label className="block text-xs">
                  Масштаб маркера на плане
                  <input
                    type="number"
                    step={0.05}
                    min={0.2}
                    className="mt-0.5 w-full border rounded px-2 py-1 text-sm"
                    defaultValue={Number(editLayerForPanel.marker_scale) || 1}
                    key={`ms-${editLayerForPanel.id}-${editLayerForPanel.updated_at}`}
                    onBlur={(e) =>
                      updateLayerMutation.mutate({ id: editLayerForPanel.id, marker_scale: Number(e.target.value) || 1 })
                    }
                  />
                </label>
                <label className="block text-xs">
                  Размер проектной маркировки (px)
                  <input
                    type="number"
                    step={1}
                    min={6}
                    className="mt-0.5 w-full border rounded px-2 py-1 text-sm"
                    defaultValue={Number(editLayerForPanel.label_font_size) || 12}
                    key={`lf-${editLayerForPanel.id}-${editLayerForPanel.updated_at}`}
                    onBlur={(e) =>
                      updateLayerMutation.mutate({ id: editLayerForPanel.id, label_font_size: Number(e.target.value) || 12 })
                    }
                  />
                </label>
                <ColorPickerField
                  key={`lb-${editLayerForPanel.id}`}
                  label="Подложка маркировки"
                  value={editLayerForPanel.label_bg_color}
                  fallback="rgba(255, 255, 255, 0.9)"
                  onCommit={(value) => updateLayerMutation.mutate({ id: editLayerForPanel.id, label_bg_color: value })}
                />
                <p className="text-[10px] text-gray-500 pt-1">Линии (для новых линий при текущем слое)</p>
                <label className="block text-xs">
                  Тип линии
                  <select
                    className="mt-0.5 w-full border rounded px-2 py-1 text-sm"
                    value={editLayerForPanel.line_algorithm ?? DEFAULT_ALGORITHM}
                    onChange={(e) =>
                      updateLayerMutation.mutate({ id: editLayerForPanel.id, line_algorithm: e.target.value })
                    }
                  >
                    {Object.values(Algorithm).map((alg) => (
                      <option key={alg} value={alg}>
                        {LINE_TYPE_LABELS[alg]}
                      </option>
                    ))}
                  </select>
                </label>
                <ColorPickerField
                  key={`lc-${editLayerForPanel.id}`}
                  label="Цвет линии"
                  value={editLayerForPanel.line_color}
                  fallback="rgba(255, 87, 51, 1)"
                  onCommit={(value) => updateLayerMutation.mutate({ id: editLayerForPanel.id, line_color: value })}
                />
                <label className="block text-xs">
                  Толщина линии
                  <input
                    type="number"
                    min={1}
                    max={24}
                    className="mt-0.5 w-full border rounded px-2 py-1 text-sm"
                    defaultValue={Number(editLayerForPanel.line_stroke_width) || 2}
                    key={`lw-${editLayerForPanel.id}-${editLayerForPanel.updated_at}`}
                    onBlur={(e) =>
                      updateLayerMutation.mutate({
                        id: editLayerForPanel.id,
                        line_stroke_width: Number(e.target.value) || 2,
                      })
                    }
                  />
                </label>
                <label className="block text-xs">
                  Пунктир (dasharray, пусто = сплошная)
                  <input
                    className="mt-0.5 w-full border rounded px-2 py-1 text-sm font-mono text-[11px]"
                    placeholder="8 4"
                    defaultValue={editLayerForPanel.line_stroke_dasharray ?? ''}
                    key={`da-${editLayerForPanel.id}-${editLayerForPanel.updated_at}`}
                    onBlur={(e) => {
                      const v = e.target.value.trim()
                      updateLayerMutation.mutate({
                        id: editLayerForPanel.id,
                        line_stroke_dasharray: v === '' ? null : v,
                      })
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </aside>
      )}

      {activeToolPanel === 'shapes' && (
        <aside className="w-80 border-r border-gray-200 bg-white flex flex-col z-10 relative">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Shapes</h2>
                <p className="text-xs text-gray-500 mt-1">Перетащите фигуру на план. Она появится на текущем слое.</p>
              </div>
              <button type="button" onClick={() => setActiveToolPanel(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {SHAPE_PRESETS.map((preset) => {
              const style = createShapeStyle(preset)
              return (
                <div
                  key={preset.type}
                  draggable
                  onDragStart={(event) => {
                    const payload = { kind: 'shape', preset }
                    event.dataTransfer.setData(DEVICE_DRAG_TYPE, JSON.stringify(payload))
                    event.dataTransfer.effectAllowed = 'move'
                    dragRef.current = payload
                  }}
                  className="border border-gray-200 rounded-lg p-3 bg-white hover:bg-blue-50 cursor-grab transition-colors select-none"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-12 flex items-center justify-center shrink-0">
                      <ShapeSvg properties={style} className="w-full h-full" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-900">{preset.label}</div>
                      <div className="text-xs text-gray-500 mt-1">Можно соединять линиями и двигать по плану.</div>
                    </div>
                  </div>
                </div>
              )
            })}
            {layersList.length > 0 && (
              <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
                Текущий слой: {layerById(layersList, activeLayerId ?? layersList[0]?.id)?.name ?? 'Основной'}.
              </p>
            )}
          </div>
        </aside>
      )}

      {activeToolPanel === 'library' && (
      <aside className="w-80 border-r border-gray-200 bg-white flex flex-col z-10 relative">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Библиотека</h2>
              <p className="text-xs text-gray-500 mt-1">Перетаскивайте экземпляры на холст.</p>
            </div>
            <button type="button" onClick={() => setActiveToolPanel(null)} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <input type="text" className="mt-3 w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Поиск устройства..." value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {/* Устройства секция */}
          {Object.entries(groupedDevices).map(([deviceType, devices]) => {
            if (devices.length === 0) return null;
            return (
              <div key={deviceType} className="section">
                <button
                  onClick={() => setShowDeviceTypeSections(prev => ({
                    ...prev,
                    [deviceType]: !prev[deviceType]
                  }))}
                  className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
                >
                  <span>{getDeviceTypeDisplayName(deviceType)} ({devices.length})</span>
                  <span className="text-gray-400">{showDeviceTypeSections[deviceType] ? '▼' : '▶'}</span>
                </button>
                
                {showDeviceTypeSections[deviceType] && (
                  <div className="space-y-2">
                    {devices.map((device: any) => (
                      <div 
                        key={device.id} 
                        className="border border-gray-200 rounded p-2 bg-white hover:bg-green-50 cursor-grab transition-colors select-none"
                        title={`Устройство ID: ${device.id}`}
                        draggable
                        onDragStart={(event) => { 
                          const payload = { 
                            device_instance_id: device.id,
                            entity_type: 'device' 
                          };
                          event.dataTransfer.setData(DEVICE_DRAG_TYPE, JSON.stringify(payload)); 
                          event.dataTransfer.effectAllowed = 'move';
                          dragRef.current = payload;
                        }}
                      >
                        <div className="font-medium text-sm text-gray-900">
                          {device.position_label || `ID ${device.id}`}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {device.spec_name || 'Без спецификации'}
                        </div>
                        {device.description && (
                          <div className="text-xs text-gray-500 mt-1">{device.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          
          {filteredDevices.length === 0 && (
            <div className="text-sm text-gray-500 text-center mt-4">Ничего не найдено.</div>
          )}
        </div>
      </aside>
      )}

      <main className="flex-1 h-full relative bg-gray-200">
        <ReactFlow
            nodes={nodes}
            edges={edgesForFlow}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnectStart={onReconnectStart}
            onReconnect={onReconnect}
            onReconnectEnd={onReconnectEnd}
            onEdgesDelete={onEdgesDelete}
            edgesReconnectable={!isReadOnly}
            reconnectRadius={12}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodeDragStop={onNodeDragStop}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onMove={handleViewportMove}
            connectionMode={ConnectionMode.Loose}
            connectionLineComponent={ConnectionLine}
            onDrop={handleDrop}
            onDragOver={(e) => { 
              e.preventDefault(); 
              if (isReadOnly) return;
              if (e.dataTransfer.types.includes('Files')) {
                e.dataTransfer.dropEffect = 'copy';
              } else {
                e.dataTransfer.dropEffect = 'move';
              }
            }}
            panOnDrag={!freehandDrawing}
            zoomOnScroll={!freehandDrawing}
            panOnScroll={false}
            selectNodesOnDrag={!freehandDrawing}
            nodesDraggable={!freehandDrawing}
            nodesConnectable={!freehandDrawing && !isReadOnly}
            fitView={!hasStoredViewport}
            minZoom={0.1}
            maxZoom={8}
            deleteKeyCode={isReadOnly ? null : ['Backspace', 'Delete']}
          >
            <Background />
            {freehandDrawing && <FreehandOverlay onComplete={handleFreehandComplete} />}
            {freehandDrawing && (
              <Panel position="top-center">
                <div className="bg-purple-600 text-white text-xs rounded-full shadow-lg px-4 py-2 flex items-center gap-3">
                  <span>Режим рисования: ведите мышью/стилусом по плану</span>
                  <button type="button" onClick={() => setFreehandDrawing(false)} className="font-semibold underline underline-offset-2">
                    Готово
                  </button>
                </div>
              </Panel>
            )}
            {selectedEdge && !isReadOnly && (
              <Panel position="bottom-center" className="w-[min(980px,calc(100vw-9rem))]">
                <div className="bg-white border border-gray-200 rounded-t-xl shadow-2xl px-4 py-3 text-sm">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">Линия</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Настройка выбранной линии на плане.</p>
                    </div>
                    <button type="button" onClick={closeSelectedEdgePanel} className="text-gray-400 hover:text-gray-700 text-lg leading-none" title="Закрыть панель">✕</button>
                  </div>

                  <div className="flex flex-wrap gap-3 items-end mb-3 pb-3 border-b border-gray-100">
                    <label className="space-y-1 min-w-[200px]">
                      <span className="block text-xs font-medium text-gray-600">Слой линии</span>
                      <select
                        value={selectedEdge.data?.layerId ?? layersList[0]?.id ?? ''}
                        onChange={(e) => setSelectedEdgeLayerId(Number(e.target.value))}
                        className="h-9 w-full rounded border border-gray-300 bg-white px-2 text-sm"
                      >
                        {layersList.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={applySelectedEdgeLayerStyle}
                      className="h-9 px-3 rounded border border-gray-300 bg-white text-sm text-gray-800 hover:bg-gray-50"
                    >
                      Применить стиль слоя
                    </button>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                    <ColorPickerField
                      label="Цвет"
                      value={selectedLineColor}
                      fallback="rgba(255, 87, 51, 1)"
                      onCommit={(value) => updateSelectedEdge((edge) => ({
                        ...edge,
                        style: {
                          ...edge.style,
                          stroke: value,
                        },
                      }))}
                    />

                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-gray-600">Тип линии</span>
                      <select
                        value={selectedEdge.data?.algorithm ?? DEFAULT_ALGORITHM}
                        onChange={(event) => updateSelectedEdge((edge) => ({
                          ...edge,
                          data: {
                            ...edge.data,
                            points: edge.data?.points ?? [],
                            algorithm: event.target.value as Algorithm,
                          },
                        }))}
                        className="h-9 w-full rounded border border-gray-300 bg-white px-2"
                      >
                        {Object.values(Algorithm).map((algorithm) => (
                          <option key={algorithm} value={algorithm}>{LINE_TYPE_LABELS[algorithm]}</option>
                        ))}
                      </select>
                    </label>

                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-gray-600">Стиль</span>
                      <select
                        value={selectedLineStyle}
                        onChange={(event) => {
                          const preset = event.target.value as LineStylePreset
                          const option = LINE_STYLE_OPTIONS.find((item) => item.value === preset)
                          updateSelectedEdge((edge) => ({
                            ...edge,
                            style: {
                              ...edge.style,
                              strokeDasharray: preset === 'custom' ? `${selectedDashNumbers.dash} ${selectedDashNumbers.gap}` : option?.dasharray,
                            },
                          }))
                        }}
                        className="h-9 w-full rounded border border-gray-300 bg-white px-2"
                      >
                        {LINE_STYLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>

                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-gray-600">Толщина</span>
                      <input
                        type="number"
                        min={1}
                        max={24}
                        step={1}
                        value={selectedLineWidth}
                        onChange={(event) => updateSelectedEdge((edge) => ({
                          ...edge,
                          style: {
                            ...edge.style,
                            strokeWidth: Math.max(1, Number(event.target.value) || DEFAULT_EDGE_STROKE_WIDTH),
                          },
                        }))}
                        className="h-9 w-full rounded border border-gray-300 px-2"
                      />
                    </label>

                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-gray-600">Штрих</span>
                      <input
                        type="number"
                        min={1}
                        max={80}
                        value={selectedDashNumbers.dash}
                        disabled={selectedLineStyle === 'solid'}
                        onChange={(event) => updateSelectedEdge((edge) => ({
                          ...edge,
                          style: {
                            ...edge.style,
                            strokeDasharray: `${Math.max(1, Number(event.target.value) || selectedDashNumbers.dash)} ${selectedDashNumbers.gap}`,
                          },
                        }))}
                        className="h-9 w-full rounded border border-gray-300 px-2 disabled:bg-gray-100 disabled:text-gray-400"
                      />
                    </label>

                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-gray-600">Пробел</span>
                      <input
                        type="number"
                        min={1}
                        max={80}
                        value={selectedDashNumbers.gap}
                        disabled={selectedLineStyle === 'solid'}
                        onChange={(event) => updateSelectedEdge((edge) => ({
                          ...edge,
                          style: {
                            ...edge.style,
                            strokeDasharray: `${selectedDashNumbers.dash} ${Math.max(1, Number(event.target.value) || selectedDashNumbers.gap)}`,
                          },
                        }))}
                        className="h-9 w-full rounded border border-gray-300 px-2 disabled:bg-gray-100 disabled:text-gray-400"
                      />
                    </label>
                  </div>

                  <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
                    <div className="flex flex-wrap items-end gap-3">
                      <label className="space-y-1 min-w-[220px] flex-1">
                        <span className="block text-xs font-medium text-gray-600">Текст меток на ребре</span>
                        <input
                          type="text"
                          value={selectedEdgeLabelText}
                          placeholder="Например: Цепь 12"
                          onChange={(event) => {
                            const text = event.target.value
                            const labels = selectedEdgeLabels.length > 0 ? selectedEdgeLabels : [createEdgeLabel(text, 0)]
                            updateSelectedEdgeLabels(labels.map((label) => ({ ...label, text })))
                          }}
                          className="h-9 w-full rounded border border-gray-300 bg-white px-2"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const text = selectedEdgeLabelText || 'Цепь'
                          const labels = selectedEdgeLabels.length > 0 ? selectedEdgeLabels : []
                          updateSelectedEdgeLabels([...labels, createEdgeLabel(text, labels.length)])
                        }}
                        className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                      >
                        {selectedEdgeLabels.length === 0 ? 'Создать метку' : 'Добавить дубль'}
                      </button>
                      {selectedEdgeLabels.length > 0 && (
                        <button
                          type="button"
                          onClick={() => updateSelectedEdgeLabels([])}
                          className="px-3 py-2 rounded bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                        >
                          Очистить метки
                        </button>
                      )}
                    </div>

                    {selectedEdgeLabels.length > 0 && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {selectedEdgeLabels.map((label, index) => (
                          <div key={label.id} className="rounded border border-blue-100 bg-white p-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-700">Метка {index + 1}</span>
                              <button
                                type="button"
                                onClick={() => updateSelectedEdgeLabels(selectedEdgeLabels.filter((item) => item.id !== label.id))}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                удалить
                              </button>
                            </div>
                            <input
                              type="range"
                              min="5"
                              max="95"
                              value={Math.round((label.position ?? 0.5) * 100)}
                              onChange={(event) => {
                                const position = clampEdgeLabelPosition(Number(event.target.value) / 100)
                                updateSelectedEdgeLabels(selectedEdgeLabels.map((item) => (
                                  item.id === label.id ? { ...item, position } : item
                                )))
                              }}
                              className="mt-1 w-full"
                            />
                            <div className="flex justify-between text-[10px] text-gray-400">
                              <span>к началу</span>
                              <span>к концу</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedEdgeLabels.length === 0 && (
                      <button type="button" onClick={createFirstSelectedEdgeLabel} className="px-3 py-2 rounded bg-blue-100 text-blue-800 hover:bg-blue-200">
                        Метка одним кликом
                      </button>
                    )}
                    <button type="button" onClick={resetSelectedEdgeRoute} className="px-3 py-2 rounded bg-gray-100 text-gray-800 hover:bg-gray-200">
                      Сбросить маршрут
                    </button>
                    <button type="button" onClick={resetSelectedEdgeVisual} className="px-3 py-2 rounded bg-gray-100 text-gray-800 hover:bg-gray-200">
                      Сбросить тип/стиль/цвет/толщину
                    </button>
                    <button type="button" onClick={deleteSelectedEdge} className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700">
                      Удалить линию
                    </button>
                  </div>
                </div>
              </Panel>
            )}
          </ReactFlow>
      </main>

      {selectedShape && selectedShapeStyle && !isReadOnly && (
        <aside className="w-96 border-l border-gray-200 bg-white p-4 flex flex-col z-10 relative">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Shape</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setSelectedShapeId(null)}>✕</button>
            </div>
            <label className="block text-sm space-y-1">
              <span className="text-gray-500">Название</span>
              <input
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                value={selectedShapeStyle.label}
                onChange={(e) => updateSelectedShapeStyle({ label: e.target.value })}
              />
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-gray-500">Тип</span>
              <select
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                value={selectedShapeStyle.shapeType}
                onChange={(e) => updateSelectedShapeStyle({ shapeType: e.target.value as FloorPlanShapeType })}
              >
                {SHAPE_PRESETS.map((preset) => (
                  <option key={preset.type} value={preset.type}>{preset.label}</option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm space-y-1">
                <span className="text-gray-500">Ширина</span>
                <input
                  type="number"
                  min={24}
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                  value={selectedShapeStyle.width}
                  onChange={(e) => updateSelectedShapeStyle({ width: Math.max(24, Number(e.target.value) || 120) })}
                />
              </label>
              <label className="block text-sm space-y-1">
                <span className="text-gray-500">Высота</span>
                <input
                  type="number"
                  min={24}
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                  value={selectedShapeStyle.height}
                  onChange={(e) => updateSelectedShapeStyle({ height: Math.max(24, Number(e.target.value) || 80) })}
                />
              </label>
            </div>
            <ColorPickerField
              key={`shape-fill-${selectedShape.id}`}
              label="Заливка"
              value={selectedShapeStyle.fill}
              fallback="#dbeafe"
              onCommit={(value) => updateSelectedShapeStyle({ fill: value })}
            />
            <ColorPickerField
              key={`shape-stroke-${selectedShape.id}`}
              label="Обводка"
              value={selectedShapeStyle.stroke}
              fallback="#2563eb"
              onCommit={(value) => updateSelectedShapeStyle({ stroke: value })}
            />
            <label className="block text-sm space-y-1">
              <span className="text-gray-500">Толщина обводки</span>
              <input
                type="number"
                min={1}
                max={24}
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                value={selectedShapeStyle.strokeWidth}
                onChange={(e) => updateSelectedShapeStyle({ strokeWidth: Math.max(1, Number(e.target.value) || 2) })}
              />
            </label>
            {layersList.length > 0 && (
              <label className="block text-sm space-y-1 pt-2">
                <span className="text-gray-500 font-medium">Слой</span>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                  value={String(resolveItemLayerId(selectedShape as any, layersList) ?? layersList[0]?.id ?? '')}
                  onChange={(e) => {
                    const layer_id = Number(e.target.value)
                    patchShapeInFloorPlanCache(selectedShape.id, { layer_id })
                    saveShapeMutation.mutate({ id: selectedShape.id, layer_id })
                  }}
                >
                  {layersList.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="pt-3 border-t">
              <button onClick={() => removeItemMutation.mutate(selectedShape.id)} className="w-full px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                Убрать shape с холста
              </button>
            </div>
          </div>
        </aside>
      )}

      {selectedShape && selectedFreehandStyle && (
        <aside className="w-96 border-l border-gray-200 bg-white p-4 flex flex-col z-10 relative">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Рисунок от руки</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setSelectedShapeId(null)}>✕</button>
            </div>
            <ColorPickerField
              key={`freehand-stroke-${selectedShape.id}`}
              label="Цвет"
              value={selectedFreehandStyle.stroke}
              fallback="#7c3aed"
              onCommit={(value) => updateSelectedFreehandStyle({ stroke: value })}
            />
            <label className="block text-sm space-y-1">
              <span className="text-gray-500">Толщина</span>
              <input
                type="number"
                min={1}
                max={48}
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                value={selectedFreehandStyle.strokeWidth}
                onChange={(e) => updateSelectedFreehandStyle({ strokeWidth: Math.max(1, Number(e.target.value) || 7) })}
              />
            </label>
            {layersList.length > 0 && (
              <label className="block text-sm space-y-1 pt-2">
                <span className="text-gray-500">Слой</span>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                  value={String(resolveItemLayerId(selectedShape as any, layersList) ?? layersList[0]?.id ?? '')}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    const layer_id = Number(e.target.value)
                    patchShapeInFloorPlanCache(selectedShape.id, { layer_id })
                    saveShapeMutation.mutate({ id: selectedShape.id, layer_id })
                  }}
                >
                  {layersList.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <p className="text-xs text-gray-500 bg-gray-50 rounded border border-gray-200 p-2">
              Размер меняется рамкой на холсте после выбора рисунка.
            </p>
            <div className="pt-3 border-t">
              <button onClick={() => removeItemMutation.mutate(selectedShape.id)} className="w-full px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                Убрать рисунок с холста
              </button>
            </div>
          </div>
        </aside>
      )}

      {selectedItem && (
        <SchemaElementInfoAside
          className="w-96 border-l border-gray-200 bg-white flex flex-col shadow-lg h-full max-h-screen"
          projectId={resolvedProjectId}
          deviceInstanceId={selectedItem.device_instance_id}
          deviceName={selectedItem.position_label || `ID ${selectedItem.device_instance_id}`}
          specificationId={selectedItem.specification_id}
          specName={selectedItem.spec_name}
          entityType={selectedItem.entity_type === 'cable' ? 'cable' : 'device'}
          description={selectedItem.description}
          knxAddress={selectedItem.knx_address}
          isReserve={Number(selectedItem.is_reserve) === 1}
          isReadOnly={isReadOnly}
          onClose={() => setSelectedItemId(null)}
          onRemove={() => removeItemMutation.mutate(selectedItem.id)}
          onDataChanged={() => {
            queryClient.invalidateQueries({ queryKey: ['floor-plan', resolvedSchemaId] })
            queryClient.invalidateQueries({ queryKey: ['floor-plan-devices', resolvedProjectId, resolvedSchemaId] })
            queryClient.invalidateQueries({ queryKey: ['floor-plan-cables', resolvedProjectId, resolvedSchemaId] })
          }}
        >
            {layersList.length > 0 && (
              <label className="block text-sm space-y-1 pt-2">
                <span className="text-gray-500 font-medium">Слой</span>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                  value={String(resolveItemLayerId(selectedItem, layersList) ?? layersList[0]?.id ?? '')}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    updateItemLayerMutation.mutate({ id: selectedItem.id, layer_id: Number(e.target.value) })
                  }
                >
                  {layersList.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
        </SchemaElementInfoAside>
      )}
      </div>
    </div>
  )
}

export default function NetworkSchemaEditor(props: NetworkSchemaEditorProps) {
  return (
    <ReactFlowProvider>
      <InnerNetworkSchemaEditor {...props} />
    </ReactFlowProvider>
  )
}